import React, { useMemo, useState } from 'react';
import type {
  DimensionsCollection,
  PanelDimensionalScores,
  Reviewers,
} from '../../../../../graphql';
import { processPanelDimensionalScores } from './PanelListTableCells';
import {
  DIMENSION_TYPE,
  type FundingSource,
  type SYSTEM_SCORE,
} from '../../../../../../Nomination/components';
import { PANEL_DETAIL_APPLICATION_TEXT } from '../../../../../lang';
import { ReviewDetailsDialog } from '../ReviewDetailsDialog';
import { getDepartmentRating } from '../panelDataHandling';

type PanelEvaluationProps = {
  panelDimensionalScores: PanelDimensionalScores[];
  reviewers: Reviewers[];
  scoringSystem: SYSTEM_SCORE;
  fundingSourceEnum: string;
  showRatingDetails: boolean;
  dimensionsCollection: DimensionsCollection[];
};

const PanelEvaluationScore: React.FunctionComponent<PanelEvaluationProps> = (
  props: PanelEvaluationProps,
) => {
  const {
    panelDimensionalScores,
    scoringSystem,
    dimensionsCollection,
    fundingSourceEnum,
    reviewers,
    showRatingDetails,
  } = props;
  const [openReviewDetailsDialog, setOpenReviewDetailsDialog] =
    useState<boolean>(false);

  const returnDetailedScores = (dimensionType: DIMENSION_TYPE): number => {
    const matchedScores = panelDimensionalScores?.find(
      dm => dm.dimension === dimensionType,
    );

    return getDepartmentRating(
      matchedScores?.systemScore,
      matchedScores?.overrideScore,
    );
  };

  const processedDimensionalScores = processPanelDimensionalScores(
    scoringSystem,
    panelDimensionalScores,
    dimensionsCollection,
  );

  const MemorizedReviewDetailsDialog = useMemo((): React.ReactNode => {
    const dimensionScores = {
      AE: returnDetailedScores(DIMENSION_TYPE.AE),
      RP: returnDetailedScores(DIMENSION_TYPE.RP),
      LS: returnDetailedScores(DIMENSION_TYPE.LS),
      CI: returnDetailedScores(DIMENSION_TYPE.CI),
      RE: returnDetailedScores(DIMENSION_TYPE.RE),
      OS: returnDetailedScores(DIMENSION_TYPE.OS),
      CD: returnDetailedScores(DIMENSION_TYPE.CD),
      US: returnDetailedScores(DIMENSION_TYPE.US),
    };

    return (
      <>
        {openReviewDetailsDialog && (
          <ReviewDetailsDialog
            openDialog={openReviewDetailsDialog}
            dialogContent={{
              fundingSource: fundingSourceEnum as FundingSource,
              reviewersScores: reviewers,
              scores: dimensionScores,
            }}
            onDialogClose={() => {
              setOpenReviewDetailsDialog(false);
            }}
          />
        )}
      </>
    );
  }, [
    openReviewDetailsDialog,
    fundingSourceEnum,
    panelDimensionalScores,
    reviewers,
  ]);

  return (
    <>
      <div className="w-full">
        <div className="grid grid-cols-[4fr,2fr,4fr,2fr] gap-4 justify-items-start">
          {processedDimensionalScores.map((d, index) => {
            return (
              <React.Fragment key={index}>
                <p className="w-full text-left">
                  {d.dimension}
                  {/* show star to highlight that it has remark bonus */}
                  <span className="text-[#951D1D]">
                    {d.hasRemark ? ' \u2605' : ''}
                  </span>
                </p>
                <div className="w-[42px] flex">
                  <div
                    className={
                      'border-2 h-[20px] w-[20px] font-bold bg-[#FFFFFF]'
                    }
                    title="Score Generated by AI"
                  >
                    {d.systemScore}
                  </div>

                  {d.isOverrideVersion && (
                    <div
                      className={`border-2 h-[20px] w-[20px] font-bold ${d.isOverrideVersion ? 'bg-[#7FFFD4]' : 'bg-[#FFFFFF]'}`}
                      title="Score Modified by Dept."
                    >
                      {d.displayScore}
                    </div>
                  )}

                  {/* {d.isOverrideVersion && `(${d.systemScore})`} */}
                </div>
              </React.Fragment>
            );
          })}
        </div>
        {showRatingDetails && (
          <div
            className="w-full text-center underline mt-4 font-bold text-[#951D1D]"
            onClick={() => {
              setOpenReviewDetailsDialog(true);
            }}
          >
            {PANEL_DETAIL_APPLICATION_TEXT.DETAIL}
          </div>
        )}
      </div>

      {MemorizedReviewDetailsDialog}
    </>
  );
};
const MemoizedPanelEvaluationScore = React.memo(PanelEvaluationScore);
export { MemoizedPanelEvaluationScore };
